/** Customer Dashboard (Demo Mode only) - the Customer Hub: the primary landing point for the
 * customer-first Customer Management workspace. Answers "which customers are being onboarded",
 * "what's the commercial readiness", "what's missing", "what's blocking", "what's the next best
 * action" and "which renewals need attention" from a single screen, then hands off into the
 * per-customer workspace (see `../CustomerWorkspace`). Replaces the old flat top-level menu
 * (Customer Overview/Contracts & Terms/Mobilisation Plans/Reporting Requirements/Contacts/
 * Commercial Model/Customer Sites) - those concepts now live as tabs inside the customer
 * workspace. All figures are static mock data, matching the "seeded illustrative numbers"
 * approach used by `src/pages/EstateManagement`. */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Chip, Grid, IconButton, InputAdornment, Stack, SvgIcon, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography, useTheme,
} from '@mui/material';
import {
  People, RocketLaunchOutlined, Autorenew, ReportProblemOutlined, Clear, Search, ArrowForward,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import { Section } from '../../../components/ui/Section';
import { TableHeader } from '../../../components/forms/TableHeader';
import { Pill } from '../../../components/shared/Pill';
import CustomPagination from '../../../components/forms/Pagination';
import {
  getAllCustomers, isOnboardingCustomer, ONBOARDING_COLOR, ONBOARDING_STATUS_LABEL, READINESS_COLOR,
  READINESS_STATUS_LABEL, RISK_COLOR,
} from '../../../mocks/data/customerManagementData';

const CustomerHub = () => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  const customers = getAllCustomers();

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return customers;
    }
    return customers.filter((customer) => customer.name.toLowerCase().includes(query) || customer.sector.toLowerCase().includes(query));
  }, [customers, search]);

  const onboardingInProgress = customers.filter((customer) => customer.onboardingStatus === 'InProgress' || customer.onboardingStatus === 'Blocked').length;
  const renewalsDue = customers.filter((customer) => customer.status === 'Renewal Due').length;
  const openBlockers = customers.reduce((total, customer) => total + customer.blockers.length, 0);

  const METRIC_TILES = [
    { label: t('customerHubTotalCustomers', 'Total clients'), value: `${customers.length}`, subtitle: t('customerHubTotalCustomersSub', 'Across all sectors'), icon: <People />, color: '#1976d2' },
    { label: t('customerHubOnboarding', 'Onboarding in progress'), value: `${onboardingInProgress}`, subtitle: t('customerHubOnboardingSub', 'Needs attention to reach go-live'), icon: <RocketLaunchOutlined />, color: '#ed6c02' },
    { label: t('customerHubRenewalsDue', 'Renewals due'), value: `${renewalsDue}`, subtitle: t('customerHubRenewalsDueSub', 'Approaching contract end'), icon: <Autorenew />, color: '#9c27b0' },
    { label: t('customerHubOpenBlockers', 'Open blockers'), value: `${openBlockers}`, subtitle: t('customerHubOpenBlockersSub', 'Across the portfolio'), icon: <ReportProblemOutlined />, color: '#d32f2f' },
  ];

  return (
    <PageArea>
      <PageTitle subTitle={t('customerHubSubTitle', 'A single view across onboarding, commercial readiness, risk and renewals for every client, with a next best action for each.')}>
        {t('customerHubTitle', 'Client Dashboard')}
      </PageTitle>

      <Grid container
        spacing={4}
        sx={{ mb: 6 }}>
        {METRIC_TILES.map((tile) => (
          <Grid item
            xs={12}
            sm={6}
            md={3}
            key={tile.label}>
            <Section style={{ p: 4, mb: 0 }}>
              <Box display="flex"
                alignItems="center"
                justifyContent="space-between">
                <Box flex={1}>
                  <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.25, fontWeight: 500 }}>
                    {tile.label}
                  </Box>
                  <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.25 }}>
                    {tile.value}
                  </Box>
                  <Box sx={{ fontSize: '12px', color: theme.palette.custom.gray[600] }}>
                    {tile.subtitle}
                  </Box>
                </Box>
                <Box sx={{ backgroundColor: `${tile.color}20`, borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', color: tile.color }}>
                  <SvgIcon fontSize="medium">{tile.icon}</SvgIcon>
                </Box>
              </Box>
            </Section>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mb: 4 }}>
        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('customerHubSearchPlaceholder', 'Search clients by name or sector')}
          size="small"
          sx={{ width: 360, mb: 0 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: search && (
              <InputAdornment position="end">
                <IconButton size="small"
                  onClick={() => setSearch('')}>
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Section>
        <TableHeader>
          {t('customerHubListTitle', 'Clients')}
          <Pill text={`${filtered.length} ${t('customerHubCountLabel', 'clients')}`} />
        </TableHeader>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('customerHubCustomer', 'Client')}</TableCell>
                <TableCell>{t('customerHubOnboardingStatus', 'Onboarding Status')}</TableCell>
                <TableCell>{t('customerHubCommercialReadiness', 'Commercial Readiness')}</TableCell>
                <TableCell>{t('customerHubRiskBlockers', 'Risk / Blockers')}</TableCell>
                <TableCell>{t('customerHubMissingInfo', 'Missing Info')}</TableCell>
                <TableCell>{t('customerHubRenewalDate', 'Renewal Date')}</TableCell>
                <TableCell>{t('customerHubNextBestAction', 'Next Best Action')}</TableCell>
                <TableCell>{t('customerHubActions', 'Actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((customer) => (
                <TableRow key={customer.id}
                  hover>
                  <TableCell>
                    <Typography variant="textSM"
                      fontWeight={600}>{customer.name}</Typography>
                    <Typography variant="textXS"
                      color="text.secondary">{customer.sector}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={t(`onboardingStatus${customer.onboardingStatus}`, ONBOARDING_STATUS_LABEL[customer.onboardingStatus])}
                      size="small"
                      color={ONBOARDING_COLOR[customer.onboardingStatus]}
                      variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip label={t(`readinessStatus${customer.commercialReadiness}`, READINESS_STATUS_LABEL[customer.commercialReadiness])}
                      size="small"
                      color={READINESS_COLOR[customer.commercialReadiness]}
                      variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row"
                      spacing={1}
                      alignItems="center">
                      <Chip label={customer.riskLevel}
                        size="small"
                        color={RISK_COLOR[customer.riskLevel]} />
                      {customer.blockers.length > 0 && (
                        <Typography variant="textXS"
                          color="text.secondary">{customer.blockers[0]}</Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {customer.missingInfo.length === 0
                      ? <Typography variant="textXS"
                        color="text.secondary">{t('customerHubNoneMissing', 'None')}</Typography>
                      : customer.missingInfo.map((item) => (
                        <Chip key={item}
                          label={item}
                          size="small"
                          color="error"
                          variant="outlined"
                          sx={{ mr: 0.5, mb: 0.5 }} />
                      ))}
                  </TableCell>
                  <TableCell>{customer.renewalDate}</TableCell>
                  <TableCell>
                    <Typography variant="textXS">{customer.nextBestAction}</Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton size="small"
                      title={t('customerHubOpenWorkspace', 'Open client workspace')}
                      onClick={() => navigate(isOnboardingCustomer(customer) ? `/customer-management/onboarding/${customer.id}` : `/customer-management/customers/${customer.id}`)}>
                      <ArrowForward fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography variant="textSM"
                      color="text.secondary">{t('customerHubNoResults', 'No clients match your search.')}</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <CustomPagination page={page}
          setPage={setPage}
          maxPages={1}
          useNoDataMessage={false}
          breakpointForChangeDisplay={120000} />
      </Section>
    </PageArea>
  );
};

export default CustomerHub;
